import clsxm from '@/lib/clsxm';

import { brandSafe, CellValue, LAST_CHECKED, OurMark } from './kit';
import type { AlternativePageProps } from './types';
import { SectionHeading, tone } from '../primitives';

// Column headers stay in view while a long table scrolls past.
const STICKY_HEAD =
  'sticky top-0 z-10 bg-white px-6 py-5 text-start shadow-[inset_0_-1px_0_rgba(15,15,40,0.07)] dark:bg-[#14141f] dark:shadow-[inset_0_-1px_0_rgba(255,255,255,0.08)]';

/**
 * The full comparison. One semantic table: on phones each row becomes a
 * card, with the column names printed above each value.
 */
export default function ComparisonTable({
  name,
  table,
}: Pick<AlternativePageProps, 'name' | 'table'>) {
  return (
    <section id='comparison' className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={brandSafe(`Easy-Peasy.AI vs ${name}, feature by feature.`)}
          tail={`Checked ${LAST_CHECKED}.`}
        />

        <div
          className={clsxm(
            'mt-12 overflow-clip rounded-[28px] max-md:overflow-visible max-md:rounded-none max-md:bg-transparent max-md:ring-0',
            tone.surface,
            tone.line,
          )}
        >
          <table className='w-full border-collapse text-start max-md:block'>
            <caption className='sr-only'>
              Easy-Peasy.AI compared with {name}
            </caption>
            <colgroup>
              <col className='w-[24%]' />
              <col className='w-[38%]' />
              <col className='w-[38%]' />
            </colgroup>
            <thead className='max-md:hidden'>
              <tr>
                <th
                  scope='col'
                  className={clsxm(
                    STICKY_HEAD,
                    'text-sm font-medium',
                    tone.quiet,
                  )}
                >
                  Feature
                </th>
                <th scope='col' className={STICKY_HEAD}>
                  <span className='flex items-center gap-2.5'>
                    <OurMark className='h-7 w-7' />
                    <span
                      className={clsxm('text-[15px] font-semibold', tone.ink)}
                    >
                      Easy-Peasy.AI
                    </span>
                  </span>
                </th>
                <th
                  scope='col'
                  className={clsxm(
                    STICKY_HEAD,
                    'text-[15px] font-semibold',
                    tone.ink,
                  )}
                >
                  {name}
                </th>
              </tr>
            </thead>
            {table.map((group) => (
              <tbody
                key={group.title}
                className='max-md:mb-8 max-md:block max-md:last:mb-0'
              >
                <tr className='max-md:block'>
                  <th
                    scope='colgroup'
                    colSpan={3}
                    className={clsxm(
                      'border-t border-[#0f0f28]/[0.07] bg-[#f8f8fb] px-6 py-3 text-start text-xs font-semibold uppercase tracking-[0.08em] dark:border-white/[0.08] dark:bg-white/[0.03] max-md:block max-md:border-0 max-md:bg-transparent max-md:px-1 max-md:pb-3 max-md:pt-0',
                      tone.quiet,
                    )}
                  >
                    {group.title}
                  </th>
                </tr>
                {group.rows.map((row) => (
                  <tr
                    key={row.key}
                    className={clsxm(
                      'border-t border-[#0f0f28]/[0.06] align-top dark:border-white/[0.07] max-md:mb-3 max-md:block max-md:rounded-2xl max-md:border-0 max-md:p-5',
                      'max-md:bg-white max-md:ring-1 max-md:ring-[#0f0f28]/[0.07] max-md:dark:bg-[#14141f] max-md:dark:ring-white/[0.08]',
                    )}
                  >
                    <th
                      scope='row'
                      className={clsxm(
                        'px-6 py-5 text-start text-[15px] font-semibold leading-6 max-md:block max-md:p-0',
                        tone.ink,
                      )}
                    >
                      {row.label}
                    </th>
                    <td
                      data-label='Easy-Peasy.AI'
                      className='px-6 py-5 max-md:mt-4 max-md:block max-md:p-0 max-md:before:mb-1.5 max-md:before:block max-md:before:text-xs max-md:before:font-semibold max-md:before:text-primary-700 max-md:before:content-[attr(data-label)] max-md:dark:before:text-primary-300'
                    >
                      <CellValue cell={row.ours} linkDocs />
                    </td>
                    <td
                      data-label={name}
                      className='px-6 py-5 max-md:mt-4 max-md:block max-md:border-t max-md:border-[#0f0f28]/[0.06] max-md:p-0 max-md:pt-4 max-md:before:mb-1.5 max-md:before:block max-md:before:text-xs max-md:before:font-semibold max-md:before:text-[#6f6f86] max-md:before:content-[attr(data-label)] max-md:dark:border-white/[0.07] max-md:dark:before:text-[#9a9ab4]'
                    >
                      <CellValue cell={row.theirs} />
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
        <p className={clsxm('mt-5 max-w-3xl text-sm leading-6', tone.quiet)}>
          Numbers in brackets link to the {name} page each fact comes from. Our
          side links to our docs. Plans and features change: check both sites
          before you decide.
        </p>
      </div>
    </section>
  );
}
